import React, { useState } from "react";
import {
  Box,
  Avatar,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Typography,
  TextField,
} from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import NotificationsActiveIcon from "@mui/icons-material/NotificationsActive";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import AddIcon from "@mui/icons-material/Add";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import CalendarTodayOutlinedIcon from "@mui/icons-material/CalendarTodayOutlined";
import CloseIcon from "@mui/icons-material/Close";
import CheckPermissions from "../../../authorization/authorization";
import { permissionsVariables } from "../../../services/Functions";
const initialUpdates = [
  {
    title: "Initial Release",
    description: "eOps updates module has been added.",
    date: "2026-04-13",
    author: "Admin User",
  },
  {
    title: "Sidebar Enhancement",
    description: "Added eOps Updates entry under management section.",
    date: "2026-04-12",
    author: "System Team",
  },
  {
    title: "Permission Alignment",
    description: "Visibility aligned with Access Management permissions.",
    date: "2026-04-11",
    author: "Ops Support",
  },
  {
    title: "UI Polishing",
    description: "Improved table readability and spacing for long records.",
    date: "2026-04-10",
    author: "Design Team",
  },
  {
    title: "Routing Update",
    description: "Navigation route configured for eOps updates page.",
    date: "2026-04-09",
    author: "Admin User",
  },
  {
    title: "Dummy Dataset",
    description: "Added sample updates to verify layout and visual theme.",
    date: "2026-04-08",
    author: "System Team",
  },
  {
    title: "Release Notes",
    description: "Prepared changelog rows for upcoming production release.",
    date: "2026-04-07",
    author: "Release Desk",
  },
  {
    title: "Color Theme",
    description: "Applied header blue and alternating row striping.",
    date: "2026-04-06",
    author: "Design Team",
  },
  {
    title: "Data Model",
    description: "Standardized columns as title, description and date.",
    date: "2026-04-05",
    author: "Ops Support",
  },
  {
    title: "Audit Trail",
    description: "Added placeholders for future publication history.",
    date: "2026-04-04",
    author: "Admin User",
  },
  {
    title: "Performance",
    description: "Optimized rendering for larger list sizes.",
    date: "2026-04-03",
    author: "System Team",
  },
  {
    title: "Content Draft",
    description: "Prepared initial content blocks for admin announcements.",
    date: "2026-04-14",
    author: "Release Desk",
  },
];

function relDate(d) {
  const diff = Math.floor((new Date() - new Date(d)) / (1000 * 60 * 60 * 24));
  if (diff === 0) return "Today";
  if (diff === 1) return "1d ago";
  return `${diff}d ago`;
}

function fullDate(d) {
  return new Date(d).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

const colors = {
  primary: "#0b5cad",
  primaryDark: "#083f7a",
  primarySoft: "#eaf3ff",
  accent: "#0ea5a3",
  surface: "#ffffff",
  surfaceHover: "#f4f8ff",
  border: "#d7e3f4",
  title: "#0b5cad",
  body: "#334155",
  muted: "#64748b",
  danger: "#d32f2f",
  dangerDark: "#b71c1c",
  dangerSoft: "#fff1f1",
};

const DEFAULT_AUTHOR = "Admin User";

const EopsUpdates = () => {
  const [rows, setRows] = useState(initialUpdates);
  const [openDialog, setOpenDialog] = useState(false);
  const [viewRow, setViewRow] = useState(null);
  const [editIndex, setEditIndex] = useState(null);
  const [editValues, setEditValues] = useState({
    title: "",
    description: "",
    date: "",
  });
  const [formValues, setFormValues] = useState({
    title: "",
    description: "",
    date: "",
  });
  const [formErrors, setFormErrors] = useState({});
  const [editErrors, setEditErrors] = useState({});
  const { permissions } = useSelector((state) => state.auth);

  const deleteItem = (title) =>
    setRows((prev) => prev.filter((u) => u.title !== title));

  const handleChange = (field, value) => {
    setFormValues((prev) => ({ ...prev, [field]: value }));
    setFormErrors((prev) => ({ ...prev, [field]: "" }));
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setFormValues({ title: "", description: "", date: "" });
    setFormErrors({});
  };

  const handleOpenView = (row) => {
    setViewRow(row);
  };

  const handleCloseView = () => {
    setViewRow(null);
  };

  const handleOpenEdit = (row, index) => {
    setEditIndex(index);
    setEditValues({
      title: row.title,
      description: row.description,
      date: row.date,
    });
    setEditErrors({});
  };

  const handleCloseEdit = () => {
    setEditIndex(null);
    setEditValues({ title: "", description: "", date: "" });
    setEditErrors({});
  };

  const handleEditChange = (field, value) => {
    setEditValues((prev) => ({ ...prev, [field]: value }));
    setEditErrors((prev) => ({ ...prev, [field]: "" }));
  };

  const validateValues = (values) => {
    const errors = {};
    if (!values.title.trim()) errors.title = "Title is required.";
    else if (values.title.trim().length < 3)
      errors.title = "Title must be at least 3 characters.";
    if (!values.description.trim())
      errors.description = "Description is required.";
    else if (values.description.trim().length < 10)
      errors.description = "Description must be at least 10 characters.";
    if (!values.date) errors.date = "Date is required.";
    return errors;
  };

  const validateForm = () => {
    return validateValues(formValues);
  };

  const handleEditSubmit = () => {
    const errors = validateValues(editValues);
    setEditErrors(errors);
    if (Object.keys(errors).length > 0 || editIndex === null) return;

    setRows((prev) =>
      prev.map((item, index) =>
        index === editIndex
          ? {
              ...item,
              title: editValues.title.trim(),
              description: editValues.description.trim(),
              date: editValues.date,
              author: DEFAULT_AUTHOR,
            }
          : item,
      ),
    );

    handleCloseEdit();
  };

  const handleSubmit = () => {
    const errors = validateForm();
    setFormErrors(errors);
    if (Object.keys(errors).length > 0) return;
    setRows((prev) => [
      {
        title: formValues.title.trim(),
        description: formValues.description.trim(),
        date: formValues.date,
        author: DEFAULT_AUTHOR,
      },
      ...prev,
    ]);
    handleCloseDialog();
  };

  return (
    <Box className="form-box">
      <Box className="application-table">
        {/* Header */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            mb: 2,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Typography
              sx={{ color: colors.primary, fontSize: "22px", fontWeight: 600 }}
            >
              eOps Updates
            </Typography>
            <Box
              sx={{
                backgroundColor: colors.primary,
                color: "#eef6ff",
                fontSize: 12,
                fontWeight: 600,
                px: 1.25,
                py: "2px",
                borderRadius: "999px",
              }}
            >
              {rows.length}
            </Box>
          </Box>
          {CheckPermissions(
            permissions,
            permissionsVariables.accessManagement,
            "eOps Updates",
            "Create",
          ) && (
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => setOpenDialog(true)}
              sx={{
                backgroundColor: colors.primary,
                "&:hover": { backgroundColor: colors.primaryDark },
                borderRadius: "8px",
                textTransform: "none",
                fontSize: 14,
                fontWeight: 600,
              }}
            >
              Add Update
            </Button>
          )}
        </Box>

        {/* Cards */}
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
          {rows
            .sort((a, b) => {
              const aIsToday = relDate(a.date) === "Today";
              const bIsToday = relDate(b.date) === "Today";
              return bIsToday - aIsToday;
            })
            .map((row, i) => {
              const isToday = relDate(row.date) === "Today";

              return (
                <Box
                  key={`${row.title}-${i}`}
                  sx={{
                    display: "flex",
                    alignItems: "flex-start",
                    gap: 1.5,
                    px: 2,
                    py: 1.75,
                    backgroundColor: colors.surface,
                    borderRadius: "10px",
                    border: `1px solid ${colors.border}`,
                    borderLeft: `4px solid ${isToday ? "#16a34a" : colors.title}`,
                    transition: "background 0.15s",
                    "&:hover": { backgroundColor: colors.surfaceHover },
                  }}
                >
                  {/* Show dot only for today's updates */}
                  {isToday && (
                    <Box
                      sx={{
                        width: 8,
                        height: 8,
                        borderRadius: "50%",
                        backgroundColor: "#16a34a",
                        mt: "6px",
                        flexShrink: 0,
                      }}
                    />
                  )}

                  {/* Bell icon avatar */}
                  <Avatar
                    sx={{
                      bgcolor: colors.primarySoft,
                      width: 42,
                      height: 42,
                      flexShrink: 0,
                    }}
                  >
                    <NotificationsActiveIcon
                      sx={{ color: colors.primary, fontSize: 20 }}
                    />
                  </Avatar>

                  {/* Content */}
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    {/* Title + relative date */}
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "flex-start",
                        justifyContent: "space-between",
                        gap: 1,
                        mb: 0.4,
                      }}
                    >
                      <Typography
                        sx={{
                          fontSize: 19,
                          fontWeight: 700,
                          color: colors.title,
                          lineHeight: 1.35,
                        }}
                      >
                        {row.title}
                      </Typography>
                      {isToday ? (
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 0.5,
                            px: 1,
                            py: 0.5,
                            backgroundColor: "#dcfce7",
                            borderRadius: "20px",
                            border: "1px solid #16a34a",
                            flexShrink: 0,
                          }}
                        >
                          <Typography
                            sx={{
                              fontSize: 12,
                              color: "#16a34a",
                              fontWeight: 600,
                            }}
                          >
                            ⭐ Today
                          </Typography>
                        </Box>
                      ) : (
                        <Typography
                          sx={{
                            fontSize: 14,
                            color: colors.muted,
                            whiteSpace: "nowrap",
                            mt: "2px",
                            flexShrink: 0,
                          }}
                        >
                          {relDate(row.date)}
                        </Typography>
                      )}
                    </Box>

                    {/* Description */}
                    <Typography
                      sx={{
                        fontSize: 18,
                        color: colors.body,
                        lineHeight: 1.55,
                      }}
                    >
                      {row.description}
                    </Typography>

                    {/* Footer: author + actions */}
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        mt: 1,
                      }}
                    >
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 1 }}
                      >
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 0.45,
                          }}
                        >
                          <CalendarTodayOutlinedIcon
                            sx={{ fontSize: 14, color: colors.muted }}
                          />
                          <Typography
                            sx={{ fontSize: 14, color: colors.muted }}
                          >
                            {fullDate(row.date)}
                          </Typography>
                        </Box>
                        <Box
                          sx={{
                            width: 4,
                            height: 4,
                            borderRadius: "50%",
                            bgcolor: "#94a3b8",
                          }}
                        />
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 0.45,
                          }}
                        >
                          <PersonOutlineIcon
                            sx={{ fontSize: 20, color: colors.muted }}
                          />
                          <Typography
                            sx={{ fontSize: 14, color: colors.muted }}
                          >
                            {DEFAULT_AUTHOR}
                          </Typography>
                        </Box>
                      </Box>

                      <Box sx={{ display: "flex", gap: 0.25 }}>
                          <IconButton
                            size="small"
                            sx={{
                              width: 32,
                              height: 32,
                              border: `1px solid ${colors.border}`,
                              borderRadius: "6px",
                              color: colors.primary,
                              "&:hover": {
                                backgroundColor: colors.primarySoft,
                              },
                            }}
                            onClick={() => handleOpenView(row)}
                          >
                            <VisibilityOutlinedIcon sx={{ fontSize: 14 }} />
                          </IconButton>
                        {CheckPermissions(
                          permissions,
                          permissionsVariables.accessManagement,
                          "eOps Updates",
                          "Update",
                        ) && (
                          <IconButton
                            size="small"
                            sx={{
                              width: 32,
                              height: 32,
                              border: `1px solid ${colors.border}`,
                              borderRadius: "6px",
                              color: colors.primary,
                              "&:hover": {
                                backgroundColor: colors.primarySoft,
                              },
                            }}
                            onClick={() => handleOpenEdit(row, i)}
                          >
                            <EditOutlinedIcon sx={{ fontSize: 14 }} />
                          </IconButton>
                        )}
                        {CheckPermissions(
                          permissions,
                          permissionsVariables.accessManagement,
                          "eOps Updates",
                          "Delete",
                        ) && (
                          <IconButton
                            size="small"
                            sx={{
                              width: 32,
                              height: 32,
                              border: `1px solid ${colors.border}`,
                              borderRadius: "6px",
                              color: colors.danger,
                              "&:hover": { backgroundColor: colors.dangerSoft },
                            }}
                            onClick={() => deleteItem(row.title)}
                          >
                            <DeleteOutlineIcon sx={{ fontSize: 14 }} />
                          </IconButton>
                        )}
                      </Box>
                    </Box>
                  </Box>
                </Box>
              );
            })}

          {rows.length === 0 && (
            <Typography
              sx={{ textAlign: "center", color: "#aaa", fontSize: 14, py: 5 }}
            >
              No updates found
            </Typography>
          )}
        </Box>

        {/* Dialog */}
        <Dialog
          open={openDialog}
          onClose={handleCloseDialog}
          fullWidth
          maxWidth="sm"
        >
          <DialogTitle
            sx={{
              backgroundColor: colors.primary,
              color: "#fff",
              fontSize: 16,
              fontWeight: 600,
              pr: 6,
            }}
          >
            Add eOps Update
            <IconButton
              onClick={handleCloseDialog}
              sx={{
                position: "absolute",
                right: 10,
                top: 10,
                color: "#fff",
                "&:hover": { backgroundColor: "rgba(255,255,255,0.15)" },
              }}
              size="small"
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </DialogTitle>
          <DialogContent>
            <Typography
              sx={{
                fontSize: 13,
                fontWeight: 600,
                color: "#555",
                mt: 1.5,
                mb: 0.5,
              }}
            >
              Title
            </Typography>
            <TextField
              fullWidth
              size="small"
              value={formValues.title}
              onChange={(e) => handleChange("title", e.target.value)}
              placeholder="Enter update title"
            />
            {formErrors.title && (
              <Typography sx={{ color: "#d32f2f", fontSize: 12, mt: 0.5 }}>
                {formErrors.title}
              </Typography>
            )}

            <Typography
              sx={{
                fontSize: 13,
                fontWeight: 600,
                color: "#555",
                mt: 2,
                mb: 0.5,
              }}
            >
              Description
            </Typography>
            <TextField
              fullWidth
              multiline
              minRows={4}
              size="small"
              value={formValues.description}
              onChange={(e) => handleChange("description", e.target.value)}
              placeholder="Enter description"
            />
            {formErrors.description && (
              <Typography sx={{ color: "#d32f2f", fontSize: 12, mt: 0.5 }}>
                {formErrors.description}
              </Typography>
            )}

            <Typography
              sx={{
                fontSize: 13,
                fontWeight: 600,
                color: "#555",
                mt: 2,
                mb: 0.5,
              }}
            >
              Date
            </Typography>
            <TextField
              type="date"
              fullWidth
              size="small"
              value={formValues.date}
              onChange={(e) => handleChange("date", e.target.value)}
            />
            {formErrors.date && (
              <Typography sx={{ color: "#d32f2f", fontSize: 12, mt: 0.5 }}>
                {formErrors.date}
              </Typography>
            )}
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button
              onClick={handleCloseDialog}
              sx={{
                backgroundColor: colors.danger,
                color: "#fff",
                textTransform: "none",
                borderRadius: "8px",
                "&:hover": { backgroundColor: colors.dangerDark },
              }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleSubmit}
              sx={{
                textTransform: "none",
                borderRadius: "8px",
                backgroundColor: colors.primary,
                "&:hover": { backgroundColor: colors.primaryDark },
              }}
            >
              Submit
            </Button>
          </DialogActions>
        </Dialog>

        <Dialog
          open={Boolean(viewRow)}
          onClose={handleCloseView}
          fullWidth
          maxWidth="sm"
        >
          <DialogTitle
            sx={{
              backgroundColor: colors.primary,
              color: "#fff",
              fontSize: 16,
              fontWeight: 600,
              pr: 6,
            }}
          >
            View Update
            <IconButton
              onClick={handleCloseView}
              sx={{
                position: "absolute",
                right: 10,
                top: 10,
                color: "#fff",
                "&:hover": { backgroundColor: "rgba(255,255,255,0.15)" },
              }}
              size="small"
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </DialogTitle>
          <DialogContent sx={{ pt: "18px !important" }}>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
              <Box>
                <Typography sx={{ fontSize: 16, color: "#64748b", mb: 0.4 }}>
                  Title
                </Typography>
                <Typography
                  sx={{ fontSize: 18, color: colors.title, fontWeight: 700 }}
                >
                  {viewRow?.title}
                </Typography>
              </Box>
              <Box>
                <Typography sx={{ fontSize: 16, color: "#64748b", mb: 0.4 }}>
                  Description
                </Typography>
                <Typography
                  sx={{ fontSize: 17, color: colors.body, lineHeight: 1.6 }}
                >
                  {viewRow?.description}
                </Typography>
              </Box>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1.5,
                  pt: 0.5,
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                  <CalendarTodayOutlinedIcon
                    sx={{ fontSize: 16, color: colors.muted }}
                  />
                  <Typography sx={{ fontSize: 14, color: colors.body }}>
                    {viewRow?.date ? fullDate(viewRow.date) : ""}
                  </Typography>
                </Box>
                <Box
                  sx={{
                    width: 4,
                    height: 4,
                    borderRadius: "50%",
                    bgcolor: "#94a3b8",
                  }}
                />
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                  <PersonOutlineIcon
                    sx={{ fontSize: 22, color: colors.muted }}
                  />
                  <Typography sx={{ fontSize: 14, color: colors.body }}>
                    {DEFAULT_AUTHOR}
                  </Typography>
                </Box>
              </Box>
            </Box>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button
              onClick={handleCloseView}
              sx={{
                textTransform: "none",
                borderRadius: "8px",
                backgroundColor: colors.primary,
                color: "#fff",
                "&:hover": { backgroundColor: colors.primaryDark },
              }}
            >
              Close
            </Button>
          </DialogActions>
        </Dialog>

        <Dialog
          open={editIndex !== null}
          onClose={handleCloseEdit}
          fullWidth
          maxWidth="sm"
        >
          <DialogTitle
            sx={{
              backgroundColor: colors.primary,
              color: "#fff",
              fontSize: 16,
              fontWeight: 600,
              pr: 6,
            }}
          >
            Edit Update
            <IconButton
              onClick={handleCloseEdit}
              sx={{
                position: "absolute",
                right: 10,
                top: 10,
                color: "#fff",
                "&:hover": { backgroundColor: "rgba(255,255,255,0.15)" },
              }}
              size="small"
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </DialogTitle>
          <DialogContent>
            <Typography
              sx={{
                fontSize: 13,
                fontWeight: 600,
                color: "#555",
                mt: 1.5,
                mb: 0.5,
              }}
            >
              Title
            </Typography>
            <TextField
              fullWidth
              size="small"
              value={editValues.title}
              onChange={(e) => handleEditChange("title", e.target.value)}
              placeholder="Enter update title"
            />
            {editErrors.title && (
              <Typography sx={{ color: "#d32f2f", fontSize: 12, mt: 0.5 }}>
                {editErrors.title}
              </Typography>
            )}

            <Typography
              sx={{
                fontSize: 13,
                fontWeight: 600,
                color: "#555",
                mt: 2,
                mb: 0.5,
              }}
            >
              Description
            </Typography>
            <TextField
              fullWidth
              multiline
              minRows={4}
              size="small"
              value={editValues.description}
              onChange={(e) => handleEditChange("description", e.target.value)}
              placeholder="Enter description"
            />
            {editErrors.description && (
              <Typography sx={{ color: "#d32f2f", fontSize: 12, mt: 0.5 }}>
                {editErrors.description}
              </Typography>
            )}

            <Typography
              sx={{
                fontSize: 13,
                fontWeight: 600,
                color: "#555",
                mt: 2,
                mb: 0.5,
              }}
            >
              Date
            </Typography>
            <TextField
              type="date"
              fullWidth
              size="small"
              value={editValues.date}
              onChange={(e) => handleEditChange("date", e.target.value)}
            />
            {editErrors.date && (
              <Typography sx={{ color: "#d32f2f", fontSize: 12, mt: 0.5 }}>
                {editErrors.date}
              </Typography>
            )}
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button
              onClick={handleCloseEdit}
              sx={{
                backgroundColor: colors.danger,
                color: "#fff",
                textTransform: "none",
                borderRadius: "8px",
                "&:hover": { backgroundColor: colors.dangerDark },
              }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleEditSubmit}
              sx={{
                textTransform: "none",
                borderRadius: "8px",
                backgroundColor: colors.primary,
                "&:hover": { backgroundColor: colors.primaryDark },
              }}
            >
              Save Changes
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </Box>
  );
};

export default EopsUpdates;
